import { ref } from 'vue'

import SurfaceEditorSurface from '~/components/surface-editor/surface.vue'
import { makeLogger, renderComponentToBlob } from '~/composables/core/render-to-image.js'

async function loadFontsFromSurface(surface, log) {
  const fontStrings = [surface.background, ...(surface.elements || [])]
    .filter(el => el?.content?.font_family)
    .map(el => el.content.font_family)

  if (!fontStrings.length) return

  log?.('google fonts: requesting', fontStrings)
  log?.('google fonts: waiting for stylesheet')
  await useGoogleFonts().loadFonts(fontStrings)
  log?.('google fonts: stylesheet ready')
}

async function resolveProjectSnapshot({ project, projectId }) {
  if (project) return project
  if (!projectId) {
    throw new Error('exportSurface: project or projectId is required')
  }

  return useSurfaceEditorApi().getProject({ projectId })
}

async function exportSurfaceFromProject({
  project,
  surfaceIndex = 0,
  mountEl = null,
  snapdomOptions = {},
  maxWidth = null,
  maxHeight = null,
  exportType,
  debug
} = {}) {
  const log = makeLogger(debug)
  const targetSurface = project?.surfaces?.[surfaceIndex]

  if (!targetSurface) {
    throw new Error(`exportSurface: no surface at index ${surfaceIndex}`)
  }

  const sourceWidth = targetSurface.width
  const sourceHeight = targetSurface.height
  const normalizedMaxWidth = Number(maxWidth)
  const normalizedMaxHeight = Number(maxHeight)
  const widthScale = Number.isFinite(normalizedMaxWidth) && normalizedMaxWidth > 0
    ? normalizedMaxWidth / sourceWidth
    : Infinity
  const heightScale = Number.isFinite(normalizedMaxHeight) && normalizedMaxHeight > 0
    ? normalizedMaxHeight / sourceHeight
    : Infinity
  const exportScale = Math.min(widthScale, heightScale, 1)
  const width = Math.max(1, Math.round(sourceWidth * exportScale))
  const height = Math.max(1, Math.round(sourceHeight * exportScale))

  log?.('begin export', { width, height, sourceWidth, sourceHeight, surfaceIndex, exportScale })

  const blob = await renderComponentToBlob({
    component: SurfaceEditorSurface,
    props: {
      surface: targetSurface,
      mode: 'export',
      exportScale
    },
    width,
    height,
    mountEl,
    prepare: (_container, renderLog) => loadFontsFromSurface(targetSurface, renderLog),
    exportType,
    snapdomOptions,
    debug
  })

  return {
    projectId: project.id,
    surfaceId: targetSurface.id,
    surfaceIndex,
    width,
    height,
    sourceWidth,
    sourceHeight,
    exportScale,
    blob
  }
}

export function useProjectExport() {
  const isExporting = ref(false)
  const lastError = ref(null)

  async function exportSurface({
    project,
    projectId,
    surfaceIndex = 0,
    mountEl = null,
    snapdomOptions = {},
    maxWidth = null,
    maxHeight = null,
    exportType,
    debug
  } = {}) {
    isExporting.value = true
    lastError.value = null

    try {
      const projectSnapshot = await resolveProjectSnapshot({ project, projectId })
      return await exportSurfaceFromProject({
        project: projectSnapshot,
        surfaceIndex,
        mountEl,
        snapdomOptions,
        maxWidth,
        maxHeight,
        exportType,
        debug
      })
    } catch (e) {
      lastError.value = e
      throw new Error(`Export failed: ${e?.message || e}`)
    } finally {
      isExporting.value = false
    }
  }

  async function exportAllSurfaces({
    project,
    projectId,
    mountEl = null,
    snapdomOptions = {},
    maxWidth = null,
    maxHeight = null,
    exportType,
    debug
  } = {}) {
    isExporting.value = true
    lastError.value = null

    const projectSnapshot = await resolveProjectSnapshot({ project, projectId })
    const surfaces = projectSnapshot?.surfaces || []

    try {
      const exportedSurfaces = []

      for (let surfaceIndex = 0; surfaceIndex < surfaces.length; surfaceIndex++) {
        const surfaceResult = await exportSurfaceFromProject({
          project: projectSnapshot,
          surfaceIndex,
          mountEl,
          snapdomOptions,
          maxWidth,
          maxHeight,
          exportType,
          debug
        })

        exportedSurfaces.push(surfaceResult)
      }

      return {
        projectId: projectSnapshot.id,
        surfaces: exportedSurfaces
      }
    } catch (e) {
      lastError.value = e
      throw new Error(`Export failed: ${e?.message || e}`)
    } finally {
      isExporting.value = false
    }
  }

  return {
    isExporting,
    lastError,
    exportSurface,
    exportAllSurfaces
  }
}
